import SearchBox from 'components/molecules/SearchBox';
import { UserContext } from 'contexts/ConfigContext';
import { useContext, useState } from 'react';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import { queryClient } from 'services/reactQueryService';
import { formatDatePreservingTimeValues, getConfig, sendNotification } from 'utils/helperMethods';
import dayjs from 'dayjs';
import { Button, Switch } from 'antd';
import { PlusSquareOutlined } from '@ant-design/icons';
import PricingSimulatorIndexTableWrapper from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import CreateEditProjectContainer from 'components/molecules/PricingSimulator/Modals/CreateEditProjectContainer';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';

export type getAllProjectsStateType = {
    search_keyword: string;
    limit: number;
    offset: number;
    sort_by: string;
    sorting_order: 'desc' | 'asc';
    user_id: string;
    project_scope: 'all' | 'self';
};

const simulationSubHeader = 'Explore scenarios, compare or create a new one';

const Projects = () => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulator = getConfig('pricingSimulator', appConfig);
    const limit = pricingSimulator?.indexPageTableLimit;
    const dataSourceList = pricingSimulator?.projectDataSource;
    const simulatedPeriodItems = pricingSimulator?.simulatedPeriodItems;
    const [searchKey, setSearchKey] = useState<string>('');
    const [tableProjectsReactQueryVariables, setTableProjectsReactQueryVariables] = useState<getAllProjectsStateType>({
        search_keyword: '',
        limit,
        offset: 0,
        sort_by: 'updated_at',
        sorting_order: 'desc',
        user_id: localStorage.getItem('email') ?? '',
        project_scope: 'all',
    });
    const [newlyCreatedProjectId, setNewlyCreatedProjectId] = useState<null | number>(null);
    const [editParams, setEditParams] = useState({});
    const [openProjectModal, setOpenProjectModal] = useState({ modalOpen: false, isEdit: false });

    const { loading: configLoading, dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);

    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        setTableProjectsReactQueryVariables({ ...tableProjectsReactQueryVariables, offset: 0, search_keyword: val });
    };

    function resetModalStates() {
        setEditParams({});
        setOpenProjectModal({ modalOpen: false, isEdit: false });
    }

    function onCreateProjectFinish(projectId: number) {
        sendNotification(
            'success',
            openProjectModal?.isEdit
                ? PricingSimulatorNotificationMessages.EDIT_PROJECT_SUCCESS
                : PricingSimulatorNotificationMessages.CREATE_PROJECT_SUCCESS,
        );
        setNewlyCreatedProjectId(projectId);
        resetModalStates();
        queryClient.refetchQueries({
            exact: true,
            queryKey: ['ps-index-table-projects', tableProjectsReactQueryVariables],
        });
        queryClient.removeQueries({
            exact: false,
            queryKey: ['ps-index-table-projects', 'ps-index-table-scenarios'],
        });
    }

    function onEdit(rowData: any) {
        parseDataForEdit(rowData);
    }

    function parseDataForEdit(rowData: any) {
        const { responseRowData } = rowData;
        const dropdownType = responseRowData?.simulation_period_type || 'custom_date';

        //find the dropdown item from config
        const simulationPeriodDropdownItem =
            simulatedPeriodItems?.find((item: any) => {
                return item?.value === dropdownType;
            }) ?? simulatedPeriodItems?.[0];

        //convert unix to local timestamp preserving time values
        //take the date value directly from config for current and financial year
        const startDateFormatted =
            simulationPeriodDropdownItem?.value === 'custom_date'
                ? formatDatePreservingTimeValues(responseRowData?.simulation_time_period?.start_timestamp)
                : dayjs(simulationPeriodDropdownItem?.dateArray?.[0]).subtract(6, 'day'); //subtracting 6 so that we get start week date

        //take the date value directly from config for current and financial year
        const endDateFormatted =
            simulationPeriodDropdownItem?.value === 'custom_date'
                ? formatDatePreservingTimeValues(responseRowData?.simulation_time_period?.end_timestamp)
                : dayjs(simulationPeriodDropdownItem?.dateArray?.[1]).subtract(6, 'day');

        const startDateTemp = dayjs(startDateFormatted).add(6, 'day').toDate();

        const endDateTemp = dayjs(endDateFormatted).add(6, 'day').toDate();
        const utcOffset = dayjs().utcOffset();

        const simulationTimePeriodSelection = JSON.stringify({
            start_timestamp: dayjs(startDateFormatted).add(utcOffset, 'm').unix(),
            end_timestamp: dayjs(endDateFormatted).add(utcOffset, 'm').unix(),
        });
        const formattedPriceEffectiveDate = formatDatePreservingTimeValues(responseRowData?.price_effective_date);
        const priceEffectiveDate = responseRowData?.price_effective_date
            ? dayjs(formattedPriceEffectiveDate).add(6, 'day').toDate()
            : null;

        const payload = {
            id: rowData?.projectId,
            projectName: rowData?.projectName,
            simulationTimePeriodSelection,
            isPrivate: rowData?.isPrivate,
            tempSimulationTimePeriodSelection: [startDateTemp, endDateTemp],
            priceEffectiveDate: [null, priceEffectiveDate],
            tempPriceEffectiveDate: [null, priceEffectiveDate],
            simulatedPeriodDropdownItem: simulationPeriodDropdownItem,
            dataSource: dataSourceList?.find((item: any) => item?.value === responseRowData?.data_source),
            // filters: {
            //     dim1: dim1Variable,
            //     dim2: dim2Variable,
            // },
            // allowCompetitors: Boolean(responseRowData?.include_competitors),
        };
        setOpenProjectModal({ isEdit: true, modalOpen: true });
        setEditParams(payload);
    }

    return (
        <>
            <div className="flex justify-between items-center">
                <div>
                    <h5 className={`font-xs m-0 font-normal text-fontGreyAlt `}>{simulationSubHeader} </h5>
                </div>

                <div className="flex items-center gap-2">
                    <SearchBox onSearch={onSearch} search={searchKey} setSearch={setSearchKey} />
                    <div className="flex items-center gap-2">
                        <p className="text-promoColour font-xxs">My Scenario</p>
                        <Switch
                            onChange={(val) => {
                                setTableProjectsReactQueryVariables({
                                    ...tableProjectsReactQueryVariables,
                                    offset: 0,
                                    project_scope: val ? 'self' : 'all',
                                });
                                queryClient.removeQueries({
                                    exact: false,
                                    queryKey: ['ps-index-table-projects'],
                                });
                            }}
                            checked={tableProjectsReactQueryVariables.project_scope === 'self'}
                        />
                    </div>
                    <Button
                        disabled={!Boolean(dimensionConfigData?.dim1DropdownList?.length)}
                        style={{ borderRadius: 4 }}
                        onClick={() => {
                            setOpenProjectModal({ modalOpen: true, isEdit: false });
                        }}
                        type="primary"
                        icon={<PlusSquareOutlined />}
                    >
                        Create New
                    </Button>
                </div>
            </div>
            <div className="my-2 border-b border-borderGreyAlt"></div>
            <PricingSimulatorIndexTableWrapper
                onEdit={onEdit}
                newlyCreatedProjectId={newlyCreatedProjectId}
                setNewlyCreatedProjectId={setNewlyCreatedProjectId}
                setTableProjectsReactQueryVariables={setTableProjectsReactQueryVariables}
                tableProjectsReactQueryVariables={tableProjectsReactQueryVariables}
            />
            <CreateEditProjectContainer
                onSubmitFinish={onCreateProjectFinish}
                open={openProjectModal.modalOpen}
                editParams={editParams}
                isEdit={openProjectModal.isEdit}
                onCancel={(e) => {
                    resetModalStates();
                }}
            />
        </>
    );
};

export default Projects;
